import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import {
  FetchPartyDataSuccessAction,
  ProfileActionTypes,
  RefreshPartyDataAction
} from '@amfam/profile/data-access/lib/+state/profile.actions';
import type { PaperlessDiscountPayload } from '@amfam/shared/models/lib/rpa/rpa.model';
import { selectCustomerGroupId } from '@amfam/shared/utility/brand/lib/+state/brand.selectors';
import { Applications } from '@amfam/shared/utility/shared-services/lib/application/application.enum';
import { ApplicationService } from '@amfam/shared/utility/shared-services/lib/application/application.service';

import { GenerateEmailConstants, GoPaperlessSuccessModel } from '../models/go-paperless.model';
import { GoPaperlessService } from '../services/go-paperless.service';
import {
  EnrollGoPaperless,
  EnrollGoPaperlessFailure,
  EnrollGoPaperlessSuccess,
  GenerateGoPaperlessEmail,
  GenerateGoPaperlessEmailFailure,
  GenerateGoPaperlessEmailSuccess,
  LoadGoPaperless,
  SendGoPaperlessAnalytics,
  UnEnrollGoPaperless,
  UnEnrollGoPaperlessFailure,
  UnEnrollGoPaperlessSuccess
} from './go-paperless.actions';

export const load = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(ProfileActionTypes.FETCH_PARTY_DATA_SUCCESS),
      map(
        (action: ReturnType<typeof FetchPartyDataSuccessAction>) =>
          action.payload?.typeOfEdeliveryStatusCode
      ),
      map(enrollStatus => LoadGoPaperless(enrollStatus))
    );
  },
  { functional: true }
);

export const enroll = createEffect(
  (
    actions$ = inject(Actions),
    goPaperlesservice = inject(GoPaperlessService),
    appService = inject(ApplicationService),
    store = inject(Store)
  ) => {
    return actions$.pipe(
      ofType(EnrollGoPaperless),
      withLatestFrom(store.select(selectCustomerGroupId)),
      map(([action, groupId]) => [action.payload, groupId] as const),
      switchMap(([payload, groupId]) => {
        return goPaperlesservice.changeEDeliveryStatus('Y').pipe(
          mergeMap(res => {
            const successModel: GoPaperlessSuccessModel = {
              status: res.status,
              isEnrolled: true,
              correlationId: payload?.correlationId
            };
            const isAdminApp = appService.isApp(Applications.MYACCOUNT_ADMIN);
            if (isAdminApp || groupId === '642') {
              return [RefreshPartyDataAction(), EnrollGoPaperlessSuccess(successModel)];
            } else {
              return [
                RefreshPartyDataAction(),
                EnrollGoPaperlessSuccess(successModel),
                GenerateGoPaperlessEmail({
                  paperlessOperation: GenerateEmailConstants.GoPaperlessOptIn,
                  correlationId: payload?.correlationId
                })
              ];
            }
          }),
          catchError(err => of(EnrollGoPaperlessFailure(err)))
        );
      })
    );
  },
  { functional: true }
);

export const unenroll = createEffect(
  (
    actions$ = inject(Actions),
    goPaperlesservice = inject(GoPaperlessService),
    appService = inject(ApplicationService),
    store = inject(Store)
  ) => {
    return actions$.pipe(
      ofType(UnEnrollGoPaperless),
      withLatestFrom(store.select(selectCustomerGroupId)),
      map(([action, groupId]) => [action.payload, groupId] as const),
      switchMap(([payload, groupId]) => {
        return goPaperlesservice.changeEDeliveryStatus('N').pipe(
          mergeMap(res => {
            const successModel: GoPaperlessSuccessModel = {
              status: res.status,
              isEnrolled: false,
              correlationId: payload?.correlationId
            };
            const isAdminApp = appService.isApp(Applications.MYACCOUNT_ADMIN);
            if (isAdminApp || groupId === '642') {
              return [RefreshPartyDataAction(), UnEnrollGoPaperlessSuccess(successModel)];
            } else {
              return [
                RefreshPartyDataAction(),
                UnEnrollGoPaperlessSuccess(successModel),
                GenerateGoPaperlessEmail({
                  paperlessOperation: GenerateEmailConstants.GoPaperlessOptOut
                })
              ];
            }
          }),
          catchError(err => of(UnEnrollGoPaperlessFailure(err)))
        );
      })
    );
  },
  { functional: true }
);

export const generateEmail = createEffect(
  (actions$ = inject(Actions), goPaperlesservice = inject(GoPaperlessService)) => {
    return actions$.pipe(
      ofType(GenerateGoPaperlessEmail),
      map(action => action.payload),
      mergeMap(payload => {
        return goPaperlesservice.generateCustomerEmail(payload).pipe(
          map(res => GenerateGoPaperlessEmailSuccess(res)),
          catchError(err => of(GenerateGoPaperlessEmailFailure(err)))
        );
      })
    );
  },
  { functional: true }
);

export const applyPaperlessDiscount = createEffect(
  (actions$ = inject(Actions), goPaperlesservice = inject(GoPaperlessService)) => {
    return actions$.pipe(
      ofType(EnrollGoPaperlessSuccess, UnEnrollGoPaperlessSuccess),
      map(action => action.payload),
      switchMap((payload: GoPaperlessSuccessModel) => {
        return goPaperlesservice
          .buildPaperlessPayload(payload.isEnrolled)
          .pipe(
            switchMap((rpaPayload: PaperlessDiscountPayload) =>
              goPaperlesservice.applyPaperlessDiscount(rpaPayload)
            )
          );
      })
    );
  },
  { dispatch: false, functional: true }
);

export const analyticsNotification = createEffect(
  (actions$ = inject(Actions), goPaperlesservice = inject(GoPaperlessService)) => {
    return actions$.pipe(
      ofType(SendGoPaperlessAnalytics),
      map(action => action.payload),
      map(payload => goPaperlesservice.sendAnalytics(payload))
    );
  },
  { dispatch: false, functional: true }
);
